// DeepSORT-lite multi-object tracker (browser/JS port of the YOLO+DeepSORT
// idea). Detections come from any detector (coco-ssd here, a YOLO tfjs model
// can be swapped in). Each track keeps:
//   - a motion model (constant-velocity box prediction)
//   - an appearance descriptor (colour histogram) for short-term re-ID, so a
//     person who is briefly occluded or crosses another keeps the SAME id
//     instead of swapping.
// Matching is greedy over a cost = motion (1-IoU) + appearance (1-cosine),
// gated so implausible pairs never match. Tracks age out after maxAge misses.
//
// All boxes are normalized (0-1) in video space.

const iou = (a, b) => {
    const ax2 = a.x + a.w
    const ay2 = a.y + a.h
    const bx2 = b.x + b.w
    const by2 = b.y + b.h
    const ix = Math.max(0, Math.min(ax2, bx2) - Math.max(a.x, b.x))
    const iy = Math.max(0, Math.min(ay2, by2) - Math.max(a.y, b.y))
    const inter = ix * iy
    const uni = a.w * a.h + b.w * b.h - inter
    return uni > 0 ? inter / uni : 0
}

const cosine = (u, v) => {
    if (!u || !v) return 0
    let dot = 0
    let nu = 0
    let nv = 0
    const n = Math.min(u.length, v.length)
    for (let i = 0; i < n; i++) {
        dot += u[i] * v[i]
        nu += u[i] * u[i]
        nv += v[i] * v[i]
    }
    return nu && nv ? dot / (Math.sqrt(nu) * Math.sqrt(nv)) : 0
}

// 4x4x4 RGB colour histogram of a detection box, sampled from a downscaled
// ImageData frame. Cheap and reasonably distinctive (clothing colour etc).
const BINS = 4
export function appearanceOf(box, imageData) {
    if (!imageData) return null
    const { data, width, height } = imageData
    const x0 = Math.max(0, Math.floor(box.x * width))
    const y0 = Math.max(0, Math.floor(box.y * height))
    const x1 = Math.min(width, Math.ceil((box.x + box.w) * width))
    const y1 = Math.min(height, Math.ceil((box.y + box.h) * height))
    const hist = new Float32Array(BINS * BINS * BINS)
    const stepX = Math.max(1, Math.floor((x1 - x0) / 12))
    const stepY = Math.max(1, Math.floor((y1 - y0) / 12))
    let count = 0
    for (let y = y0; y < y1; y += stepY) {
        for (let x = x0; x < x1; x += stepX) {
            const i = (y * width + x) * 4
            const r = (data[i] >> 6) & 3
            const g = (data[i + 1] >> 6) & 3
            const b = (data[i + 2] >> 6) & 3
            hist[r * 16 + g * 4 + b] += 1
            count++
        }
    }
    if (count) for (let i = 0; i < hist.length; i++) hist[i] /= count
    return hist
}

export default class ObjectTracker {
    constructor(opts = {}) {
        this.maxAge = opts.maxAge ?? 30
        this.minHits = opts.minHits ?? 2
        this.iouGate = opts.iouGate ?? 0.15
        this.appGate = opts.appGate ?? 0.5
        this.wIou = opts.wIou ?? 0.6
        this.wApp = opts.wApp ?? 0.4
        this.tracks = []
        this._nextId = 1
    }

    reset() {
        this.tracks = []
        this._nextId = 1
    }

    // detections: [{ class, score, x, y, w, h }] (normalized). imageData: a
    // downscaled frame for appearance. Returns confirmed tracks.
    update(detections, imageData) {
        // 1. Predict every track forward by its velocity.
        for (const t of this.tracks) {
            t.box = {
                x: t.box.x + t.vel.x,
                y: t.box.y + t.vel.y,
                w: t.box.w,
                h: t.box.h,
            }
        }

        // 2. Appearance for each detection.
        const dets = detections.map((d) => ({
            ...d,
            box: { x: d.x, y: d.y, w: d.w, h: d.h },
            app: appearanceOf({ x: d.x, y: d.y, w: d.w, h: d.h }, imageData),
        }))

        const nBefore = this.tracks.length

        // 3. Feasible (track, det) pairs with combined cost.
        const pairs = []
        this.tracks.forEach((t, ti) => {
            dets.forEach((d, di) => {
                if (t.class && d.class && t.class !== d.class) return
                const ov = iou(t.box, d.box)
                const sim = cosine(t.app, d.app)
                if (ov < this.iouGate && sim < this.appGate) return
                const cost = this.wIou * (1 - ov) + this.wApp * (1 - sim)
                pairs.push({ ti, di, cost })
            })
        })
        pairs.sort((a, b) => a.cost - b.cost)

        // 4. Greedy assignment.
        const tUsed = new Set()
        const dUsed = new Set()
        for (const p of pairs) {
            if (tUsed.has(p.ti) || dUsed.has(p.di)) continue
            tUsed.add(p.ti)
            dUsed.add(p.di)
            const t = this.tracks[p.ti]
            const d = dets[p.di]
            const prevCx = t.box.x + t.box.w / 2
            const prevCy = t.box.y + t.box.h / 2
            // Blend box toward detection; update velocity from centre motion.
            t.box = {
                x: t.box.x * 0.4 + d.box.x * 0.6,
                y: t.box.y * 0.4 + d.box.y * 0.6,
                w: t.box.w * 0.4 + d.box.w * 0.6,
                h: t.box.h * 0.4 + d.box.h * 0.6,
            }
            const cx = t.box.x + t.box.w / 2
            const cy = t.box.y + t.box.h / 2
            t.vel = {
                x: t.vel.x * 0.5 + (cx - prevCx) * 0.5,
                y: t.vel.y * 0.5 + (cy - prevCy) * 0.5,
            }
            // Appearance EMA.
            if (d.app) {
                if (!t.app) t.app = d.app
                else
                    for (let i = 0; i < t.app.length; i++)
                        t.app[i] = t.app[i] * 0.9 + d.app[i] * 0.1
            }
            t.score = d.score
            t.class = d.class
            t.hits += 1
            t.misses = 0
        }

        // 5. New tracks for unmatched detections.
        dets.forEach((d, di) => {
            if (dUsed.has(di)) return
            this.tracks.push({
                id: this._nextId++,
                box: d.box,
                vel: { x: 0, y: 0 },
                app: d.app,
                class: d.class,
                score: d.score,
                hits: 1,
                misses: 0,
            })
        })

        // 6. Age pre-existing unmatched tracks; drop the stale ones. (Tracks
        // created this frame have index >= nBefore and are never aged now.)
        this.tracks.forEach((t, ti) => {
            if (ti < nBefore && !tUsed.has(ti)) t.misses += 1
        })
        this.tracks = this.tracks.filter((t) => t.misses <= this.maxAge)

        // 7. Report confirmed tracks (seen enough, currently visible).
        return this.tracks
            .filter((t) => t.hits >= this.minHits && t.misses === 0)
            .map((t) => ({
                id: t.id,
                class: t.class,
                score: t.score,
                x: t.box.x,
                y: t.box.y,
                w: t.box.w,
                h: t.box.h,
                cx: t.box.x + t.box.w / 2,
                cy: t.box.y + t.box.h / 2,
            }))
    }
}
